/* eslint-disable react/prop-types */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import Shimmer from 'react-shimmer-effect';
import IcomoonReact from 'icomoon-react';
import ReadOnlyExerciseItem from './ReadOnlyExerciseItem';
import {
  setViewType,
  setSelectedDate,
  setSelectedMonth,
  setSelectedWeekViewDayToggle,
  setWeekViewExpandedDay,
} from '../../ducks/calendarActions';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 4px;
  padding-top: ${(props) => !props.hasBefore && '14px'};
  flex: 1;
  overflow-y: auto;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  /* Custom scrollbar for month view */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`;

const RecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  border-radius: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: #F8F8F8;
  margin-top: ${(props) => !props.isBefore && '5px'};
`;

const IconContainerBefore = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainer = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  margin-left: 2px;
`;

const LineContainer = styled('div')`
  position: relative;
  flex-grow: 1;
  margin-left: 5px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
    display: flex;
    align-items: center;
  }
  width: 100%;
  overflow: hidden;
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 13px;
`;

const MonthDayContent = ({ date, exercises, isLoading }) => {
  const dispatch = useDispatch();
  const formatDate = (d) => `${moment(d).format('YYYY-MM-DD')}`;
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts,
  );
  const athleteAddedItems = useSelector(
    (state) => state.calendar.data.calendarItems[formatDate(date)]?.athleteAddedItems,
  );

  const handleDayClick = () => {
    // Reset week view toggle if needed (matches CalendarCalendarPicker pattern)
    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedWeekViewDayToggle('Everyday'));
    }

    // Find the Sunday of the week for the clicked date to maintain week arrangement
    const sundayOfWeek = moment(date).startOf('week');
    const dateToSet = sundayOfWeek.toDate();
    dispatch(setSelectedDate(dateToSet));

    // Update selected month to match the clicked date
    const selectedMonthNum = moment(date).month() + 1;
    dispatch(setSelectedMonth(selectedMonthNum));

    // Switch to week view
    const dateFormatted = date.format('YYYY-MM-DD');
    dispatch(setViewType('weekView'));
    dispatch(setWeekViewExpandedDay(dateFormatted));
  };

  const hasBefore = beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0;

  if (isLoading) {
    return (
      <ContentContainer onClick={handleDayClick}>
        {[1, 2, 3].map((_, i) => (
          <Shimmer key={i}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', marginBottom: 4 }} />
          </Shimmer>
        ))}
      </ContentContainer>
    );
  }

  // Find the index of the first session_break
  const firstSessionBreakIndex = exercises.findIndex((ex) => ex.type === 'session_break');

  return (
    <ContentContainer onClick={handleDayClick} hasBefore={hasBefore}>
      {beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader isBefore>
          <IconContainerBefore>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='recurring-circle'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Recurring (
                {beforeRecurringWorkouts[formatDate(date)].length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainerBefore>
        </RecurringHeader>
        ) : null}
      {exercises.map((exercise, index) => (
        <ReadOnlyExerciseItem
          key={`${date.format('YYYY-MM-DD')}-${exercise.id || index}`}
          content={exercise}
          index={index}
          exercises={exercises}
          date={date}
          hasSessionBreakBackground={
            firstSessionBreakIndex !== -1 && index >= firstSessionBreakIndex
          }
        />
      ))}
      {afterRecurringWorkouts[formatDate(date)]
      && afterRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader>
          <IconContainer>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='recurring-circle'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Recurring (
                {afterRecurringWorkouts[formatDate(date)].length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainer>
        </RecurringHeader>
        ) : null}
      {athleteAddedItems
      && athleteAddedItems.length > 0 ? (
        <RecurringHeader>
          <IconContainer>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='user-checkmark'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Athlete Added Items (
                {athleteAddedItems.length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainer>
        </RecurringHeader>
        ) : null}
    </ContentContainer>
  );
};

export default MonthDayContent;
